import React, { useCallback, useContext, useEffect, useState } from 'react'
import { AlertTriangle, CheckCircle, Clock, Flag, Phone, RefreshCw, ShieldCheck, Zap } from 'lucide-react'
import { BASE_URL_API } from '../../References/urls'
import AuthContext from '../../store/auth-context'
import FlagFraudModal from '../../Components/ReferralManagement/FlagFraudModal'
import styles from './VerificationQueueScreen.module.css'

const REFRESH_INTERVAL_MS = 120_000

// ─── UTILS ───────────────────────────────────────────────────────────────────
function formatTime(dateStr) {
    if (!dateStr) return '—'
    const d = new Date(String(dateStr).replace(' ', 'T'))
    if (Number.isNaN(d.getTime())) return '—'
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })
}

function lastAttemptLabel(lastAttempt) {
    if (!lastAttempt) return null
    const statusMap = {
        answered_and_verified: 'Answered and Verified',
        answered_rescheduled: 'Answered and Rescheduled',
        no_answer: 'No answer',
        busy: 'Busy',
        switched_off: 'Switched off',
        invalid_number: 'Wrong number',
        callback_requested: 'Callback requested',
    }
    const label = statusMap[lastAttempt.status] || lastAttempt.status
    return `Last: ${label} · ${formatTime(lastAttempt.attemptedOn)}`
}

// ─── LOG CALL ATTEMPT MODAL ──────────────────────────────────────────────────
function LogVerificationAttemptModal({ referralId, referredPhone, onClose, onSuccess }) {
    const authCtx = useContext(AuthContext)
    const [callStatus, setCallStatus] = useState('no_answer')
    const [notes, setNotes] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState('')

    const ATTEMPT_STATUSES = [
        { value: 'answered_and_verified', label: 'Answered and Verified' },
        { value: 'answered_rescheduled', label: 'Answered and Rescheduled' },
        { value: 'no_answer', label: 'No answer' },
        { value: 'busy', label: 'Busy' },
        { value: 'switched_off', label: 'Switched off' },
        { value: 'invalid_number', label: 'Wrong number' },
        { value: 'callback_requested', label: 'Callback requested' },
    ]

    const handleSubmit = async () => {
        setError('')
        setIsSubmitting(true)
        try {
            const res = await fetch(BASE_URL_API + '/referralLogVerificationAttempt', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    recruiterId: authCtx.userIdentifier,
                    referralId,
                    contactMethod: 'phone_call',
                    callStatus: callStatus,
                    notes,
                }),
            })
            const data = await res.json()
            if (data.success === true) {
                onSuccess()
            } else {
                if (data.message === 'token Expired, please login again!') authCtx.logout()
                else setError(data.message || 'Failed to log attempt.')
            }
        } catch {
            setError('Network error. Please try again.')
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <span className={styles.modalTitle}>Log Call Attempt</span>
                    <button className={styles.iconBtn} onClick={onClose} disabled={isSubmitting}>✕</button>
                </div>
                <div className={styles.modalBody}>
                    <div className={styles.modalPhone}>
                        <Phone size={13} />
                        <span>{referredPhone}</span>
                        <span className={styles.modalRefId}>{referralId}</span>
                    </div>
                    <div className={styles.formField}>
                        <label className={styles.formLabel}>Call outcome</label>
                        <div className={styles.statusGrid}>
                            {ATTEMPT_STATUSES.map(s => (
                                <button
                                    key={s.value}
                                    type="button"
                                    className={`${styles.statusOption} ${callStatus === s.value ? styles.statusOptionActive : ''}`}
                                    onClick={() => setCallStatus(s.value)}
                                    disabled={isSubmitting}
                                >
                                    {s.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className={styles.formField}>
                        <label className={styles.formLabel}>Notes</label>
                        <textarea
                            className={styles.formTextarea}
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            placeholder="Phone ring hua, koi nahi utha..."
                            disabled={isSubmitting}
                            rows={3}
                        />
                    </div>
                    {error && <div className={styles.errorText}>{error}</div>}
                </div>
                <div className={styles.modalFooter}>
                    <button className={styles.btnPrimary} onClick={handleSubmit} disabled={isSubmitting}>
                        {isSubmitting ? 'Logging...' : 'Log Attempt'}
                    </button>
                    <button className={styles.btnSecondary} onClick={onClose} disabled={isSubmitting}>Cancel</button>
                </div>
            </div>
        </div>
    )
}

// ─── MARK VERIFIED CONFIRM MODAL ─────────────────────────────────────────────
function MarkVerifiedConfirmModal({ referralId, referrerName, candidateName, onClose, onSuccess }) {
    const authCtx = useContext(AuthContext)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState('')

    const handleConfirm = async () => {
        setError('')
        setIsSubmitting(true)
        try {
            const res = await fetch(BASE_URL_API + '/referralMarkVerified', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    recruiterId: authCtx.userIdentifier,
                    referralId,
                }),
            })
            const data = await res.json()
            if (data.success === true) {
                onSuccess()
            } else {
                if (data.message === 'token Expired, please login again!') authCtx.logout()
                else setError(data.message || 'Failed to mark as verified.')
            }
        } catch {
            setError('Network error. Please try again.')
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <span className={styles.modalTitle}>Mark as Verified</span>
                    <button className={styles.iconBtn} onClick={onClose} disabled={isSubmitting}>✕</button>
                </div>
                <div className={styles.modalBody}>
                    <div className={styles.confirmIcon}>
                        <ShieldCheck size={32} color="#16a34a" />
                    </div>
                    <p className={styles.confirmText}>
                        Confirm verification for <strong>{candidateName || referralId}</strong>?
                        {referrerName && <> Referred by <strong>{referrerName}</strong>.</>}
                    </p>
                    <p className={styles.confirmSubtext}>
                        This will mark the referral as verified and trigger the reward flow.
                    </p>
                    {error && <div className={styles.errorText}>{error}</div>}
                </div>
                <div className={styles.modalFooter}>
                    <button className={styles.btnSuccess} onClick={handleConfirm} disabled={isSubmitting}>
                        {isSubmitting ? 'Verifying...' : 'Yes, Mark Verified'}
                    </button>
                    <button className={styles.btnSecondary} onClick={onClose} disabled={isSubmitting}>Cancel</button>
                </div>
            </div>
        </div>
    )
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
const VerificationQueueScreen = () => {
    const authCtx = useContext(AuthContext)
    const [queue, setQueue] = useState([])
    const [summary, setSummary] = useState(null)
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1, hasMore: false })
    const [page, setPage] = useState(1)
    const [isFetching, setIsFetching] = useState(false)
    const [lastUpdated, setLastUpdated] = useState(null)
    const [secondsAgo, setSecondsAgo] = useState(0)
    const [logAttemptModal, setLogAttemptModal] = useState(null)
    const [markVerifiedModal, setMarkVerifiedModal] = useState(null)
    const [flagFraudModal, setFlagFraudModal] = useState(null)
    const [toast, setToast] = useState(null)

    const showToast = (message, type = 'success') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 3500)
    }

    const fetchQueue = useCallback(async (p) => {
        setIsFetching(true)
        try {
            const res = await fetch(BASE_URL_API + '/referralGetVerificationQueue', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    recruiterId: authCtx.userIdentifier,
                    page: p,
                    limit: 20,
                }),
            })
            const data = await res.json()
            if (data.success === true) {
                setQueue(data.response?.queue || [])
                setSummary(data.response?.summary || null)
                setPagination(data.response?.pagination || { page: 1, limit: 20, total: 0, totalPages: 1, hasMore: false })
                setLastUpdated(Date.now())
                setSecondsAgo(0)
            } else {
                if (data.message === 'token Expired, please login again!') authCtx.logout()
            }
        } catch (err) {
            console.error('fetchQueue error:', err)
        } finally {
            setIsFetching(false)
        }
    }, [authCtx.token, authCtx.userIdentifier])

    useEffect(() => {
        fetchQueue(page)
    }, [page, fetchQueue])

    useEffect(() => {
        const id = setInterval(() => fetchQueue(page), REFRESH_INTERVAL_MS)
        return () => clearInterval(id)
    }, [fetchQueue, page])

    useEffect(() => {
        const id = setInterval(() => {
            if (lastUpdated) setSecondsAgo(Math.floor((Date.now() - lastUpdated) / 1000))
        }, 1000)
        return () => clearInterval(id)
    }, [lastUpdated])

    const handleLogSuccess = () => {
        setLogAttemptModal(null)
        fetchQueue(page)
    }

    const handleVerifiedSuccess = () => {
        setMarkVerifiedModal(null)
        fetchQueue(page)
    }

    const handleFlagFraudDone = (msg, isError) => {
        setFlagFraudModal(null)
        if (msg) showToast(msg, isError ? 'error' : 'success')
        fetchQueue(page)
    }

    const isWithinWindow = summary?.isWithinWindow ?? true
    const isInitialLoad = isFetching && queue.length === 0

    return (
        <div className={styles.mainWrapper}>
            {logAttemptModal && (
                <LogVerificationAttemptModal
                    referralId={logAttemptModal.referralId}
                    referredPhone={logAttemptModal.referredPhone}
                    onClose={() => setLogAttemptModal(null)}
                    onSuccess={handleLogSuccess}
                />
            )}
            {markVerifiedModal && (
                <MarkVerifiedConfirmModal
                    referralId={markVerifiedModal.referralId}
                    referrerName={markVerifiedModal.referrerName}
                    candidateName={markVerifiedModal.candidateName}
                    onClose={() => setMarkVerifiedModal(null)}
                    onSuccess={handleVerifiedSuccess}
                />
            )}
            {flagFraudModal && (
                <FlagFraudModal
                    prefillReferralId={flagFraudModal.referralId}
                    onClose={() => setFlagFraudModal(null)}
                    onDone={handleFlagFraudDone}
                />
            )}

            {toast && (
                <div className={`${styles.toast} ${toast.type === 'error' ? styles.toastError : styles.toastSuccess}`}>
                    {toast.type === 'error' ? <AlertTriangle size={13} /> : <CheckCircle size={13} />}
                    {toast.message}
                </div>
            )}

            {/* ── HEADER ─────────────────────────────────────────── */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <span className={styles.pageTitle}>Verification Queue</span>
                    <div className={styles.refreshIndicator}>
                        {isFetching
                            ? <RefreshCw size={11} className={styles.spinIcon} />
                            : lastUpdated && <span className={styles.lastUpdatedText}>Updated {secondsAgo}s ago</span>
                        }
                    </div>
                </div>

                {summary && (
                    <div className={styles.summaryBar}>
                        <div className={styles.summaryChip}>
                            <span className={styles.summaryNum}>{summary.totalPending}</span>
                            <span className={styles.summaryLabel}>Pending</span>
                        </div>
                        {summary.slaBreachCount > 0 && (
                            <div className={`${styles.summaryChip} ${styles.chipSla}`}>
                                <AlertTriangle size={11} />
                                <span className={styles.summaryNum}>{summary.slaBreachCount}</span>
                                <span className={styles.summaryLabel}>SLA Breach</span>
                            </div>
                        )}
                        <div className={styles.summaryChip}>
                            <Clock size={11} />
                            <span className={styles.summaryLabel}>Avg wait {summary.avgWaitMinutes} min</span>
                        </div>
                        <div className={`${styles.summaryChip} ${isWithinWindow ? styles.chipOpen : styles.chipClosed}`}>
                            <span className={`${styles.windowDot} ${isWithinWindow ? styles.windowDotOpen : styles.windowDotClosed}`} />
                            <span>Window: {summary.windowStart}–{summary.windowEnd} · {isWithinWindow ? 'OPEN' : 'CLOSED'}</span>
                        </div>
                    </div>
                )}
            </div>

            {/* ── OUT-OF-WINDOW BANNER ────────────────────────────── */}
            {!isWithinWindow && summary && (
                <div className={styles.windowBanner}>
                    <AlertTriangle size={13} />
                    Calling hours: {summary.windowStart}–{summary.windowEnd}. Queue visible but calls should not be made now.
                </div>
            )}

            {/* ── QUEUE BODY ──────────────────────────────────────── */}
            <div className={styles.queueBody}>
                {isInitialLoad ? (
                    <div className={styles.loadingState}>
                        <RefreshCw size={22} className={styles.spinIcon} />
                        <span>Loading verification queue...</span>
                    </div>
                ) : queue.length === 0 ? (
                    <div className={styles.emptyState}>
                        <CheckCircle size={40} color="#16a34a" />
                        <div className={styles.emptyTitle}>Queue saaf hai! Koi pending verification nahi hai ✅</div>
                    </div>
                ) : (
                    <div className={styles.queueList}>
                        {queue.map(item => (
                            <div
                                key={item.referralId}
                                className={`${styles.queueRow} ${item.isSLABreach ? styles.rowSLABreach : ''}`}
                            >
                                <div className={styles.rowMain}>
                                    <div className={styles.rowLeft}>
                                        <div className={styles.referralId}>{item.referralId}</div>
                                        <div className={styles.candidateName}>
                                            {item.candidateName || item.referredPhone}
                                        </div>
                                        <div className={styles.referrerInfo}>
                                            Referred by <strong>{item.referrerName}</strong> · {item.referrerPhone}
                                        </div>
                                        <div className={styles.metaLine}>
                                            <Clock size={10} />
                                            <span>Waiting {item.minutesWaiting} min</span>
                                            <span className={styles.dot}>·</span>
                                            <span>Registered {String(item.registeredOn).substring(0, 10)}</span>
                                            {item.lastAttempt && (
                                                <>
                                                    <span className={styles.dot}>·</span>
                                                    <span className={styles.lastAttemptText}>{lastAttemptLabel(item.lastAttempt)}</span>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    <div className={styles.rowRight}>
                                        <div className={styles.badgeRow}>
                                            {item.isSLABreach && (
                                                <span className={styles.badgeSla}>
                                                    <AlertTriangle size={9} /> SLA Breach
                                                </span>
                                            )}
                                            {item.isRewardExpiring && (
                                                <span className={styles.badgeExpiry}>
                                                    <Zap size={9} /> Expires in {item.daysUntilExpiry}d
                                                </span>
                                            )}
                                            {item.isFraudFlagged && (
                                                <span className={styles.badgeFraud}>
                                                    Fraud Flagged
                                                </span>
                                            )}
                                            <span className={styles.badgeAttempts}>
                                                Attempt {item.attemptsCount}/5
                                            </span>
                                            <span className={`${styles.badgeStatus} ${item.verificationStatus === 'in_progress' ? styles.badgeInProgress : styles.badgePending}`}>
                                                {String(item.verificationStatus).replace('_', ' ')}
                                            </span>
                                        </div>

                                        <div className={styles.actionRow}>
                                            <button
                                                className={styles.btnLog}
                                                disabled={item.isFraudFlagged}
                                                onClick={() => setLogAttemptModal({ referralId: item.referralId, referredPhone: item.referredPhone })}
                                                title={item.isFraudFlagged ? 'Disabled: Fraud flagged' : undefined}
                                            >
                                                <Phone size={11} /> Log Call Attempt
                                            </button>
                                            <button
                                                className={styles.btnVerify}
                                                disabled={item.isFraudFlagged}
                                                onClick={() => setMarkVerifiedModal({ referralId: item.referralId, referrerName: item.referrerName, candidateName: item.candidateName })}
                                                title={item.isFraudFlagged ? 'Disabled: Fraud flagged' : undefined}
                                            >
                                                <ShieldCheck size={11} /> Mark Verified
                                            </button>
                                            <button
                                                className={styles.btnFlagFraud}
                                                disabled={item.isFraudFlagged}
                                                onClick={() => setFlagFraudModal({ referralId: item.referralId })}
                                                title={item.isFraudFlagged ? 'Already fraud flagged' : undefined}
                                            >
                                                <Flag size={11} /> Flag Fraud
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* ── PAGINATION ──────────────────────────────────────── */}
            {pagination.total > 0 && (
                <div className={styles.paginationBar}>
                    <span className={styles.paginationInfo}>
                        Showing {queue.length} of {pagination.total}
                    </span>
                    <div className={styles.pageControls}>
                        <button
                            className={styles.pageBtn}
                            disabled={page <= 1}
                            onClick={() => setPage(p => p - 1)}
                        >
                            ← Prev
                        </button>
                        <span className={styles.pageNum}>Page {pagination.page} of {pagination.totalPages || 1}</span>
                        <button
                            className={styles.pageBtn}
                            disabled={!pagination.hasMore}
                            onClick={() => setPage(p => p + 1)}
                        >
                            Next →
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default VerificationQueueScreen
